import { useState } from 'react'
import { Link } from 'react-router-dom'
import { CogIcon, LandmarkIcon, ScrollTextIcon, type LucideIcon } from 'lucide-react'

type IconType = LucideIcon

const categories: {
  name: string
  tools: { title: string; description: string; link: string; icon: IconType }[]
}[] = [
  {
    name: 'ISO',
    tools: [
      {
        title: 'Bitmap Parser',
        description: 'Parse and calculate ISO 8583 bitmap fields',
        link: '/tools/iso8583-bitmap',
        icon: LandmarkIcon,
      },
      {
        title: 'Field Parser',
        description: 'Parse ISO 8583 data elements and formats',
        link: 'https://app.accezzminds.com/login',
        icon: LandmarkIcon,
      },
    ],
  },
  {
    name: 'EMV',
    tools: [
      {
        title: 'TLV Parser',
        description: 'Parse TLV data and inspect tags with values',
        link: '/tools/tlv',
        icon: CogIcon,
      },
      {
        title: 'Host Log Viewer',
        description: 'View EMV L3 host logs in a user-friendly format',
        link: 'https://app.accezzminds.com/login',
        icon: ScrollTextIcon,
      },
    ],
  },
]

export default function Tools() {
  const [hovered, setHovered] = useState<string | null>(null)
  const [search, setSearch] = useState('')

  const filtered = categories
    .map((cat) => ({
      ...cat,
      tools: cat.tools.filter((t) =>
        t.title.toLowerCase().includes(search.toLowerCase()),
      ),
    }))
    .filter(
      (cat) =>
        cat.name.toLowerCase().includes(search.toLowerCase()) ||
        cat.tools.length > 0,
    )

  return (
    <main className="mx-auto max-w-5xl px-6 py-12 lg:px-8">
      <div className="mb-8 text-center">
        <h1 className="text-3xl font-bold text-gray-900">Developer Tools</h1>
        <p className="mt-2 text-base text-gray-500">
          EMV and ISO tools to help you build and debug payment applications.
        </p>
      </div>

      {/* Search */}
      <div className="mb-8">
        <input
          type="text"
          placeholder="Search tools..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full rounded-xl border border-gray-200 px-4 py-3 text-gray-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
        />
      </div>

      {filtered.length > 0 ? (
        filtered.map((cat, ci) => (
          <div key={ci} className="mb-10">
            <h2 className="mb-4 text-lg font-bold text-indigo-600">{cat.name}</h2>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {cat.tools.map((tool, ti) => {
                const uid = `${ci}-${ti}`
                const card = (
                  <div
                    onMouseEnter={() => setHovered(uid)}
                    onMouseLeave={() => setHovered(null)}
                    className={`relative flex h-44 flex-col justify-between rounded-xl border p-5 shadow-sm transition-all duration-300 ${
                      hovered === uid
                        ? 'scale-105 border-indigo-400 shadow-lg'
                        : 'scale-100 border-gray-200'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
                        <tool.icon size={18} />
                      </div>
                      <h3 className="truncate text-base font-semibold text-gray-900">
                        {tool.title}
                      </h3>
                    </div>
                    <p className="mt-3 line-clamp-2 flex-grow text-sm text-gray-500">
                      {tool.description}
                    </p>
                    <div className="mt-3 text-sm font-medium text-indigo-600 hover:underline">
                      Explore →
                    </div>
                  </div>
                )
                return tool.link.startsWith('http') ? (
                  <a key={ti} href={tool.link}>
                    {card}
                  </a>
                ) : (
                  <Link key={ti} to={tool.link}>
                    {card}
                  </Link>
                )
              })}
            </div>
          </div>
        ))
      ) : (
        <p className="text-center text-gray-500">No tools found</p>
      )}
    </main>
  )
}
